import React, { useEffect, useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { Menu, X, Phone } from "lucide-react";

export default function Navbar() {
  const { lang, setLanguage, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "gallery", label: t.nav.gallery },
    { id: "testimonials", label: t.nav.testimonials },
    { id: "areas", label: t.nav.areas },
    { id: "contact", label: t.nav.contact },
  ];

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-neutral-900" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
        <button
          data-testid="logo-link"
          onClick={() => scrollTo("top")}
          className="flex items-center gap-3 group flex-shrink-0"
        >
          <span className="w-11 h-11 sm:w-12 sm:h-12 grid place-items-center bg-black border border-neutral-800 rounded-sm overflow-hidden group-hover:border-orange-500 transition-colors">
            <img
              src="https://customer-assets.emergentagent.com/job_solid-construction-1/artifacts/inlh5dj3_C87BBE06-A887-4651-BCBA-A1A8D4641067.png"
              alt="Black Rock Construction Services LLC logo"
              className="w-full h-full object-cover"
            />
          </span>
          <span className="flex flex-col leading-none text-left">
            <span className="font-display uppercase tracking-tight text-white text-base sm:text-lg">Black Rock</span>
            <span className="font-mono-label uppercase tracking-[0.18em] text-[9px] sm:text-[10px] text-neutral-500 mt-1 whitespace-nowrap">Construction Services LLC</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-xs xl:text-sm font-medium text-neutral-300 hover:text-orange-500 transition-colors uppercase tracking-wide whitespace-nowrap"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <div className="flex items-center bg-neutral-900 border border-neutral-800 rounded-sm overflow-hidden">
            <button
              data-testid="lang-toggle-en"
              onClick={() => setLanguage("en")}
              className={`px-2.5 py-1.5 text-xs font-bold font-mono-label ${lang === "en" ? "bg-orange-500 text-black" : "text-neutral-400 hover:text-white"}`}
            >EN</button>
            <button
              data-testid="lang-toggle-es"
              onClick={() => setLanguage("es")}
              className={`px-2.5 py-1.5 text-xs font-bold font-mono-label ${lang === "es" ? "bg-orange-500 text-black" : "text-neutral-400 hover:text-white"}`}
            >ES</button>
          </div>

          <a
            data-testid="header-call-btn"
            href="tel:+12055383654"
            className="hidden xl:inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-orange-500 transition-colors whitespace-nowrap"
          >
            <Phone size={16} /> (205) 538-3654
          </a>

          <button
            data-testid="header-cta-btn"
            onClick={() => scrollTo("contact")}
            className="hidden md:inline-flex items-center bg-orange-500 hover:bg-orange-600 text-black font-bold uppercase tracking-wide text-xs px-4 py-2.5 rounded-sm transition-colors"
          >
            {t.nav.cta}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-black border-t border-neutral-900">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="text-left text-base font-semibold text-neutral-200 hover:text-orange-500 uppercase tracking-wide"
              >
                {l.label}
              </button>
            ))}
            <a
              data-testid="mobile-call"
              href="tel:+12055383654"
              className="text-left text-base font-semibold text-orange-500"
            >(205) 538-3654</a>
            <button
              data-testid="mobile-cta"
              onClick={() => scrollTo("contact")}
              className="self-start bg-orange-500 text-black font-bold uppercase tracking-wide text-xs px-4 py-3 rounded-sm"
            >{t.nav.cta}</button>
          </div>
        </div>
      )}
    </header>
  );
}
